import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      setPaymentData(response.data);

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-[#0A0A0A] pt-20 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <div className="loader mx-auto mb-6" />
          <h1 className="font-serif text-3xl text-[#F5F2EB] mb-2">Processing Payment...</h1>
          <p className="text-[#A3988F]">Please wait while we confirm your payment</p>
        </motion.div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-[#0A0A0A] pt-20 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center px-6 max-w-lg"
        >
          <div className="w-24 h-24 bg-[#E5D3B3]/10 rounded-full flex items-center justify-center mx-auto mb-8">
            <CheckCircle className="w-12 h-12 text-[#E5D3B3]" />
          </div>
          <h1 className="font-serif text-4xl text-[#F5F2EB] mb-4">Payment Successful!</h1>
          <p className="text-[#A3988F] mb-8">
            Thank you for your purchase. Your order has been confirmed and you will receive an email confirmation shortly.
          </p>
          {paymentData && (
            <div className="luxury-card rounded-xl p-6 mb-8 text-left">
              <p className="text-sm text-[#A3988F] mb-1">Amount Paid</p>
              <p className="text-2xl font-semibold text-[#E5D3B3]" data-testid="payment-amount">
                ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
              </p>
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/orders" className="btn-primary inline-flex items-center justify-center space-x-2" data-testid="view-orders-btn">
              <span>View Orders</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/fashion" className="btn-secondary inline-flex items-center justify-center space-x-2" data-testid="continue-shopping-btn">
              <span>Continue Shopping</span>
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  // Error/Expired states
  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-20 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center px-6 max-w-lg"
      >
        <div className="w-24 h-24 bg-[#FAD6C6]/10 rounded-full flex items-center justify-center mx-auto mb-8">
          <XCircle className="w-12 h-12 text-[#FAD6C6]" />
        </div>
        <h1 className="font-serif text-4xl text-[#F5F2EB] mb-4">
          {status === 'expired' ? 'Payment Expired' : 'Payment Issue'}
        </h1>
        <p className="text-[#A3988F] mb-8">
          {status === 'expired' 
            ? 'Your payment session has expired. Please try again.'
            : 'There was an issue processing your payment. Please try again or contact support.'}
        </p>
        <Link to="/cart" className="btn-primary inline-flex items-center justify-center space-x-2" data-testid="try-again-btn">
          <span>Try Again</span>
          <ArrowRight className="w-4 h-4" />
        </Link>
      </motion.div>
    </div>
  );
};

export default CheckoutSuccessPage;
